import React, { FC, useEffect, useRef, useState } from 'react';
import { Setting } from '../App';
import classes from '../asset/css/main.module.css';
import { ChangeDeliveryDateModal } from '../components/change-delivery-date-modal/ChangeDeliveryDateModal';
import { MessageModal } from '../components/message-modal/MessageModal';
import { Header } from '../components/subscription-detail/Header';
import { Menu } from '../components/subscription-detail/Menu';
import { OneTimeBanner } from '../components/subscription-detail/OneTimeBanner';
import { ProductListPaused } from '../components/subscription-detail/ProductListPaused';
import { ProductListPickup } from '../components/subscription-detail/ProductListPickup';
import { ShippingProductList } from '../components/subscription-detail/ShippingProudctList';
import { useSubscriptionAddProducts } from '../hooks/use-subscription-add-products';
import { useSubscriptionDetail } from '../hooks/use-subscription-detail';
import { useSubscriptionLine } from '../hooks/use-subscription-line';
import { useUpdateDeliveryDate } from '../hooks/use-update-delivery-date';
import { MessageContentViewType } from '../types/view/message-content-view-type';
import { ProductInfoViewType } from '../types/view/product-info-view-type';
import { RecommendProductInfoViewType } from '../types/view/recommend-product-info-view-type';
import { OverrideUrlItemType } from '../types/view/postmessage-type';
import { SubscriptionStatus } from '../types/api/subscriptions-api-type';
import { PauseStatus } from '../components/subscription-detail/PauseStatus';
import { useSubscriptionSummary } from '../hooks/use-subscription-summary';

export const SubscriptionDetail: FC<{ setting: Setting }> = ({ setting }) => {
  const [subscriptionContractId, setSubscriptionContractId] = useState('');
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus>('ACTIVE');

  const [registeredProducts, setRegisteredProducts] = useState<ProductInfoViewType[]>([]);
  useEffect(() => {
    console.log('registeredProducts changed!', registeredProducts);
  }, [registeredProducts]);
  const [pausedProducts, setPausedProducts] = useState<ProductInfoViewType[]>([]);
  useEffect(() => {
    console.log('pausedProducts changed!', pausedProducts);
  }, [pausedProducts]);
  const [recommendProducts, setRecommendProducts] = useState<RecommendProductInfoViewType[]>([]);
  useEffect(() => {
    console.log('recommendProducts changed!', recommendProducts);
  }, [recommendProducts]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    console.log('loading changed!', loading);
  }, [loading]);
  const [loadCompleted, setLoadCompleted] = useState(false);
  const [cartItems, setCartItems] = useState<{ variantId: string; quantity: number; sellingPlanId: string }[]>([]);

  const [changeDeliveryDateModalActive, setChangeDeliveryDateModalActive] = useState(false);

  const [messageModalActive, setMessageModalActive] = useState(false);
  const [messageContent, setMessageContent] = useState<MessageContentViewType>();

  const { subscription, setSubscription, getSubscriptionDetail } = useSubscriptionDetail(
    setting,
    setSubscriptionContractId,
    setRegisteredProducts,
    setPausedProducts,
    setRecommendProducts,
    setLoadCompleted,
    setSubscriptionStatus,
  );
  const { summary, subscriptionDetail } = useSubscriptionSummary(setting, subscription);

  const root = useRef<HTMLDivElement>(null);
  useEffect(() => {
    setting.resize(root.current?.clientHeight ?? 0);
  }, [setting, root.current?.clientHeight, summary, pausedProducts, recommendProducts]);

  // スクロール量を親windowから受け取る
  const [scrollOffset, setScrollOffset] = useState(0);
  useEffect(() => {
    const parentScrollEvent = (event: MessageEvent) => {
      const { pageYOffset } = event.data;
      if (!pageYOffset) return;

      setScrollOffset(pageYOffset);
    };
    window.addEventListener('message', parentScrollEvent);

    return () => window.removeEventListener('message', parentScrollEvent);
  }, []);

  // 親windowからビューポートの高さなどを受け取る
  // オリジン制約により、parent.window.innerHeightなど参照できないため
  const [parentViewportHeight, setParentViewportHeight] = useState(0);
  const [marginToTop, setMarginToTop] = useState(0);
  useEffect(() => {
    const parentResizeEvent = (event: MessageEvent) => {
      const { viewportHeight, marginToTop } = event.data;
      if (!viewportHeight || !marginToTop) return;

      setParentViewportHeight(viewportHeight);
      setMarginToTop(marginToTop);
    };
    window.addEventListener('message', parentResizeEvent);

    return () => window.removeEventListener('message', parentResizeEvent);
  }, []);

  // お届け予定商品の遷移先URL変更のために変更先URLを受け取る
  const [overrideUrlItems, setOverrideUrlItems] = useState<OverrideUrlItemType[]>([]);
  useEffect(() => {
    const recieveOverrideUrlItemsEvent = (event: MessageEvent) => {
      const { action, items } = event.data;
      if (action !== "loadOverrideUrlItems" || !items || items.length === 0) return;

      setOverrideUrlItems([...overrideUrlItems, ...items]);
    };
    window.addEventListener('message', recieveOverrideUrlItemsEvent);

    return () => window.removeEventListener('message', recieveOverrideUrlItemsEvent);
  }, []);

  useEffect(() => {
    window.parent.postMessage({ action: 'prepared' }, '*');
  }, []);

  const { addCartProducts } = useSubscriptionAddProducts(setting);

  const failed = (message: string) => {
    setMessageContent({
      title: '内部エラー',
      body: 'BOXプランの更新に失敗しました。',
    });
    setMessageModalActive(true);

    console.log(message);
    setLoading(false);
  };

  const addProductSuccess = (showModal: boolean, status?: string) => {
    const body =
      status === 'replace'
        ? '初回割引商品(クーポン利用も含む)を購入済のため、通常商品をBOXプランに追加しました。'
        : '商品をBOXプランに追加しました。';

    if (showModal) {
      setMessageContent({
        title: 'BOXプランの更新',
        body,
      });
      setMessageModalActive(true);
    }

    getSubscriptionDetail(
      () => {
        setLoading(false);
      },
      (e) => {
        console.log(e);
        setLoading(false);
      }
    );
  };

  useEffect(() => {
    const messageEvent = (e: MessageEvent) => {
      if (!e.data.cartItems) return;

      setCartItems(e.data.cartItems);
    };
    window.addEventListener('message', messageEvent);

    return () => window.removeEventListener('message', messageEvent);
  }, []);

  useEffect(() => {
    if (cartItems.length === 0 || !loadCompleted) return;

    const status: string = '';
    addCartProducts(cartItems, () => addProductSuccess(true, status), failed);
  }, [loadCompleted, cartItems]);

  const { updateDeliveryDate } = useUpdateDeliveryDate(
    setting,
    subscriptionDetail,
    summary,
    () => {
      setChangeDeliveryDateModalActive(false);
      getSubscriptionDetail(
        () => setLoading(false),
        (e) => {
          console.log(e);
          setLoading(false);
        }
      );
    },
    failed
  );

  const removeProductSuccess = async (lineId: string) => {
    // const newRegisteredProducts = registeredProducts.map((product) => {
    //   if (product.lineId === lineId) {
    //     delete product.lineId;

    //     return {
    //       ...product,
    //       quantity: 0,
    //       active: false,
    //     };
    //   }

    //   return product;
    // });

    // const newPausedProducts = pausedProducts.map((product) => {
    //   if (product.lineId === lineId) {
    //     delete product.lineId;

    //     return {
    //       ...product,
    //       quantity: 0,
    //       active: false,
    //     };
    //   }

    //   return product;
    // });

    // const newRecommendProducts = recommendProducts.map((product) => {
    //   if (product.lineId === lineId) {
    //     delete product.lineId;

    //     return {
    //       ...product,
    //       quantity: 0,
    //       active: false,
    //     };
    //   }

    //   return product;
    // });

    // const registeredTotal = newRegisteredProducts.reduce((sum, product) => {
    //   if (!product.active) return sum;

    //   return sum + product.price * product.quantity;
    // }, 0);

    // const registeredOriginalTotal = newRegisteredProducts.reduce((sum, product) => {
    //   if (!product.active) return sum;

    //   return sum + product.originalPrice * product.quantity;
    // }, 0);

    // const pausedTotal = newPausedProducts.reduce((sum, product) => {
    //   if (!product.active) return sum;

    //   return sum + product.price * product.quantity;
    // }, 0);

    // const recommendTotal = newRecommendProducts.reduce((sum, product) => {
    //   if (!product.active) return sum;

    //   return sum + product.price * product.quantity;
    // }, 0);

    // setRegisteredProducts(newRegisteredProducts);
    // setPausedProducts(newPausedProducts);
    // setRecommendProducts(newRecommendProducts);

    // console.log('setSummary in 338');
    // // サマリの合計金額を更新する
    // setSummary({
    //   ...summary!,
    //   totalPrice: summary!.deliveryPrice + registeredTotal + pausedTotal + recommendTotal,
    //   totalOriginalPrice: summary!.deliveryPrice + registeredOriginalTotal + pausedTotal + recommendTotal,
    // });

    setLoading(false);
  };

  const updateQuantitySuccess = (lineId: string, quantity: number) => {
    // 数量を更新する
    // const newRegisteredProducts = registeredProducts.map((product) => {
    //   if (lineId !== product.lineId) return product;

    //   return {
    //     ...product,
    //     quantity,
    //   };
    // });

    // const newPausedProducts = pausedProducts.map((product) => {
    //   if (lineId !== product.lineId) return product;

    //   return {
    //     ...product,
    //     quantity,
    //   };
    // });

    // const newRecommendProducts = recommendProducts.map((product) => {
    //   if (lineId !== product.lineId) return product;

    //   return {
    //     ...product,
    //     quantity,
    //   };
    // });

    // const registeredTotal = newRegisteredProducts.reduce((sum, product) => {
    //   if (!product.active) return sum;

    //   return sum + product.price * product.quantity;
    // }, 0);

    // const registeredOriginalTotal = newRegisteredProducts.reduce((sum, product) => {
    //   if (!product.active) return sum;

    //   return sum + product.originalPrice * product.quantity;
    // }, 0);

    // const pausedTotal = newPausedProducts.reduce((sum, product) => {
    //   if (!product.active) return sum;

    //   return sum + product.price * product.quantity;
    // }, 0);

    // const recommendTotal = newRecommendProducts.reduce((sum, product) => {
    //   if (!product.active) return sum;

    //   return sum + product.price * product.quantity;
    // }, 0);

    // setRegisteredProducts(newRegisteredProducts);
    // setPausedProducts(newPausedProducts);
    // setRecommendProducts(newRecommendProducts);

    // console.log('setSummary in 407');
    // // サマリの合計金額を更新する
    // setSummary({
    //   ...summary!,
    //   totalPrice: summary!.deliveryPrice + registeredTotal + pausedTotal + recommendTotal,
    //   totalOriginalPrice: summary!.deliveryPrice + registeredOriginalTotal + pausedTotal + recommendTotal,
    // });

    setLoading(false);
  };

  const { updateQuantity, removeProduct, addProduct } = useSubscriptionLine(setting, setSubscription);
  const updateQuantityFunc = (lineId: string, quantity: number) => {
    setLoading(true);

    if (quantity > 0) {
      updateQuantity(
        subscriptionContractId,
        lineId,
        quantity,
        () => {
          updateQuantitySuccess(lineId, quantity);
        },
        setSubscriptionContractId,
        setRegisteredProducts,
        setPausedProducts,
        setRecommendProducts,
        failed
      );
    } else {
      removeProduct(
        subscriptionContractId,
        lineId,
        () => {
          removeProductSuccess(lineId);
        },
        setSubscriptionContractId,
        setRegisteredProducts,
        setPausedProducts,
        setRecommendProducts,
        failed
      );
    }
  };

  const cancelSubscriptionFunc = (lineId?: string) => {
    removeProduct(
      subscriptionContractId,
      lineId!,
      () => {
        removeProductSuccess(lineId!);
      },
      setSubscriptionContractId,
      setRegisteredProducts,
      setPausedProducts,
      setRecommendProducts,
      failed
    );
  };

  useEffect(() => {
    const clickAddToBoxEvent = (event: MessageEvent) => {
      if (Boolean(event.data.addToBoxItem) === false) return;

      const showModal = Boolean(event.data.addToBoxItem.replaced);
      const status = event.data.addToBoxItem.replaced ? 'replace' : '';
      const { planId, variantId } = event.data.addToBoxItem;

      if (Boolean(planId) && Boolean(variantId)) {
        addProduct(
          planId,
          variantId,
          1,
          () => addProductSuccess(showModal, status),
          setSubscriptionContractId,
          setRegisteredProducts,
          setPausedProducts,
          setRecommendProducts,
          failed
        );
      }
    };
    window.addEventListener('message', clickAddToBoxEvent);

    return () => window.removeEventListener('message', clickAddToBoxEvent);
  }, [addProduct]);

  // 停止中商品から特定タグ付与商品を間引く処理
  useEffect(() => {
    window.parent.postMessage({action: 'updatePausedProducts', pausedProducts:pausedProducts, registeredProducts:registeredProducts}, '*');
  }, [pausedProducts]);

  useEffect(() => {
    const updatePausedProductsEvent = (event: MessageEvent) => {
      if (Boolean(event.data.updatePausedProductsData) === false) return;
      if (event.data.updatePausedProductsData.isDecimated) {
        setPausedProducts(event.data.updatePausedProductsData.products);
      }
    };
    window.addEventListener('message', updatePausedProductsEvent);

    return () => window.removeEventListener('message', updatePausedProductsEvent);
  }, []);

  // ピックアップ商品から購入済み初回割商品を間引く処理
  useEffect(() => {
    window.parent.postMessage({action: 'updateRecommendProducts', recommendProducts}, '*');
  }, [recommendProducts]);

  useEffect(() => {
    const updateRecommendProductsEvent = (event: MessageEvent) => {
      if (Boolean(event.data.updateRecommendProductsData) === false) return;
      if (event.data.updateRecommendProductsData.isDecimated) {
        setRecommendProducts(event.data.updateRecommendProductsData.products);
      }
    };
    window.addEventListener('message', updateRecommendProductsEvent);

    return () => window.removeEventListener('message', updateRecommendProductsEvent);
  }, []);

  return (
    <>
      <div className={classes.mypage_subsc_container} ref={root}>
        { subscriptionStatus === 'PAUSED' ? (
          <PauseStatus
            token={setting.token}
            summary={summary}
            products={registeredProducts}
            contractId={subscriptionContractId}
            setSubscriptionStatus={setSubscriptionStatus}
          />
        ) : subscriptionStatus === 'ACTIVE' ? (
          <>
            <Header summary={summary} />

            <ShippingProductList
              summary={summary}
              products={registeredProducts}
              overrideUrlItems={overrideUrlItems}
              loading={loading}
              cancelSubscriptionFunc={cancelSubscriptionFunc}
              updateQuantityFunc={updateQuantityFunc}
              setChangeDeliveryDateModalFunc={setChangeDeliveryDateModalActive}
              setMessageModalActive={setMessageModalActive}
              setMessageContent={setMessageContent}
            />

            <ProductListPickup
              pickUpProducts={recommendProducts}
              loading={loading}
              setting={setting}
              addProductSuccess={addProductSuccess}
              failed={failed}
              setSubscriptionContractId={setSubscriptionContractId}
              setRegisteredProducts={setRegisteredProducts}
              setPausedProducts={setPausedProducts}
              setRecommendProducts={setRecommendProducts}
              setSubscription={setSubscription}
            />
            <ProductListPaused
              pausedProducts={pausedProducts}
              loading={loading}
              setting={setting}
              addProductSuccess={addProductSuccess}
              failed={failed}
              setSubscriptionContractId={setSubscriptionContractId}
              setRegisteredProducts={setRegisteredProducts}
              setPausedProducts={setPausedProducts}
              setRecommendProducts={setRecommendProducts}
            />
            <OneTimeBanner />
          </>
        ) : <></> }

        <Menu/>
      </div>
      <ChangeDeliveryDateModal
        active={changeDeliveryDateModalActive}
        scrollOffset={scrollOffset}
        parentViewportHeight={parentViewportHeight}
        marginToTop={marginToTop}
        summary={summary}
        updateDeliveryDate={updateDeliveryDate}
        closeFunc={() => setChangeDeliveryDateModalActive(false)}
      />
      <MessageModal
        active={messageModalActive}
        scrollOffset={scrollOffset}
        parentViewportHeight={parentViewportHeight}
        marginToTop={marginToTop}
        closeFunc={() => setMessageModalActive(false)}
        messageContent={messageContent}
      />
    </>
  );
};
